import React from "react";
import axios from "axios";
import { Link as RouterLink } from "react-router-dom";
import {
  Box,
  Button,
  Container,
  CssBaseline,
  Link,
  MenuItem,
  TextField,
  Typography,
} from "@material-ui/core";
import { makeStyles } from "@material-ui/core/styles";
import { COUNTRY_OPTIONS } from "utils/countries";
import logo from "assets/logo.png";

const useStyles = makeStyles((theme) => ({
  root: {
    minHeight: "100vh",
    display: "flex",
    alignItems: "center",
    justifyContent: "center",
    padding: theme.spacing(4, 2),
  },
  content: {
    width: "100%",
    display: "flex",
    flexDirection: "column",
    alignItems: "center",
  },
  logo: {
    height: 72,
    marginBottom: theme.spacing(2),
  },
  spaceAfter: {
    marginBottom: 15,
  },
  form: {
    width: "100%",
  },
  field: {
    marginTop: theme.spacing(2),
  },
  submit: {
    marginTop: theme.spacing(3),
    marginBottom: theme.spacing(1),
    backgroundColor: "#dcd8d8",
  },
  link: {
    color: theme.palette.text.secondary,
  },
}));

const initialFormData = {
  team_name: "",
  city: "",
  country: "",
  requester_name: "",
  requester_email: "",
  message: "",
};

export const RequestAccess = () => {
  const classes = useStyles();
  const [formData, setFormData] = React.useState(initialFormData);
  const [errors, setErrors] = React.useState({});
  const [submitting, setSubmitting] = React.useState(false);
  const [submitted, setSubmitted] = React.useState(false);

  const handleChange = (event) => {
    const { name, value } = event.target;
    if (name in errors) {
      setErrors({ ...errors, [name]: undefined });
    }
    setFormData({ ...formData, [name]: value });
  };

  const handleSubmit = (event) => {
    event.preventDefault();
    setSubmitting(true);
    axios
      .post("/api/team/request/", formData)
      .then(() => {
        setSubmitted(true);
      })
      .catch((err) => {
        setSubmitting(false);
        if (err && err.response && err.response.data) {
          const data = err.response.data;
          // The per-IP throttle's own message is DRF boilerplate - say it plainly.
          if (err.response.status === 429 && /throttled/i.test(data.detail || "")) {
            setErrors({ detail: "Too many requests from this device - please try again in a while." });
          } else {
            setErrors(data);
          }
        }
      });
  };

  if (submitted) {
    return (
      <Box className={classes.root} component="main">
        <CssBaseline />
        <Container maxWidth="xs">
          <div className={classes.content} style={{ textAlign: "center" }}>
            <a href="/">
              <img src={logo} alt="PlanPraise" className={classes.logo} />
            </a>
            <Typography component="h1" variant="h5" className={classes.spaceAfter}>
              Check your email
            </Typography>
            <Typography variant="body1" color="textSecondary" paragraph>
              Thanks, {formData.requester_name || "friend"} - we've sent a link to{" "}
              {formData.requester_email || "your email"}. Click it to confirm your
              email address; your request isn't sent to us until you do.
            </Typography>
            <Typography variant="body2" color="textSecondary">
              Once you've confirmed, we'll review it and email you again when it's
              approved, with a link to set up your team.
            </Typography>
            <Box mt={3}>
              <Link component={RouterLink} to="/login" className={classes.link}>
                Back to sign in
              </Link>
            </Box>
          </div>
        </Container>
      </Box>
    );
  }

  return (
    <Box className={classes.root} component="main">
      <CssBaseline />
      <Container maxWidth="xs">
        <div className={classes.content}>
          <a href="/">
            <img src={logo} alt="PlanPraise" className={classes.logo} />
          </a>
          <Typography component="h1" variant="h5" className={classes.spaceAfter}>
            Request access to PlanPraise
          </Typography>
          <Typography
            variant="body2"
            color="textSecondary"
            align="center"
            className={classes.spaceAfter}
          >
            Starting a new team? Tell us a bit about it below - we'll review
            your request and email you an invite link once it's approved.
          </Typography>

          {"non_field_errors" in errors && (
            <Typography
              variant="caption"
              color="error"
              display="block"
              align="center"
              className={classes.spaceAfter}
            >
              {errors.non_field_errors}
            </Typography>
          )}

          <form className={classes.form} onSubmit={handleSubmit}>
            <TextField
              variant="outlined"
              fullWidth
              label="Team name"
              name="team_name"
              value={formData.team_name}
              onChange={handleChange}
              error={"team_name" in errors}
              helperText={"team_name" in errors && errors.team_name}
            />
            <TextField
              className={classes.field}
              variant="outlined"
              fullWidth
              label="City"
              name="city"
              value={formData.city}
              onChange={handleChange}
              error={"city" in errors}
              helperText={"city" in errors && errors.city}
            />
            <TextField
              className={classes.field}
              variant="outlined"
              fullWidth
              select
              label="Country"
              name="country"
              value={formData.country}
              onChange={handleChange}
              error={"country" in errors}
              helperText={"country" in errors && errors.country}
            >
              {COUNTRY_OPTIONS.map((option) => (
                <MenuItem key={option.code} value={option.code}>
                  {option.name}
                </MenuItem>
              ))}
            </TextField>
            <TextField
              className={classes.field}
              variant="outlined"
              fullWidth
              label="Your name"
              name="requester_name"
              value={formData.requester_name}
              onChange={handleChange}
              error={"requester_name" in errors}
              helperText={"requester_name" in errors && errors.requester_name}
            />
            <TextField
              className={classes.field}
              variant="outlined"
              fullWidth
              type="email"
              label="Your email"
              name="requester_email"
              value={formData.requester_email}
              onChange={handleChange}
              error={"requester_email" in errors}
              helperText={"requester_email" in errors && errors.requester_email}
            />
            <TextField
              className={classes.field}
              variant="outlined"
              fullWidth
              multiline
              minRows={3}
              label="Anything else we should know? (optional)"
              name="message"
              value={formData.message}
              onChange={handleChange}
              error={"message" in errors}
              helperText={"message" in errors && errors.message}
            />
            {errors.detail && (
              <Typography variant="body2" color="error" style={{ marginTop: 8 }}>
                {errors.detail}
              </Typography>
            )}
            <Button
              type="submit"
              fullWidth
              variant="contained"
              className={classes.submit}
              disabled={submitting}
            >
              Request Access
            </Button>
          </form>
          <Link component={RouterLink} to="/login" className={classes.link}>
            Already have an account? Sign in
          </Link>
        </div>
      </Container>
    </Box>
  );
};

export default RequestAccess;
